import { styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { EFlightCategory, ENOPOrganType } from '#shared';

import { EZLayers, ZLayer } from '../../data-context';
import { useGeoMap } from '../../GeoMap';
import { isGeoPointVisible } from '../../helpers';
import { useSelectHighlightedAircraftRegistration } from '../../widgetManager';
import { useOverlay } from '../model';

import { AviationAircraft } from './AviationAircraft';
import { AviationAirport } from './AviationAirport';
import { AviationFurtherPath } from './AviationFurtherPath';
import { AviationTrail } from './AviationTrail';

const PREFIX = 'TSMED-AviationElements';
const aviationElementsClasses = {
  airportMark: `${PREFIX}-airportMark`,
  airportMarkFlightCategory: `${PREFIX}-airportMarkFlightCategory`,
  airportMarkIFR: `${PREFIX}-airportMark${EFlightCategory.IFR}`,
  airportMarkLIFR: `${PREFIX}-airportMark${EFlightCategory.LIFR}`,
  airportMarkMVFR: `${PREFIX}-airportMark${EFlightCategory.MVFR}`,
  airportMarkVFR: `${PREFIX}-airportMark${EFlightCategory.VFR}`,
  airportLabel: `${PREFIX}-airportLabel`,
  airportAircraftsWrapper: `${PREFIX}-airportAircraftsWrapper`,
  airportAircraftsMark: `${PREFIX}-airportAircraftsMark`,
  trails: `${PREFIX}-trails`,
  trail: `${PREFIX}-trail`,
  furtherPaths: `${PREFIX}-furtherPaths`,
  furtherPath: `${PREFIX}-furtherPath`,
  aircraftOrphanWrapper: `${PREFIX}-aircraftOrphanWrapper`,
  aircraftOrphan: `${PREFIX}-aircraftOrphan`,
};

const Root = styled('g')(({ theme: { palette, utils } }) => ({
  [`.${aviationElementsClasses.airportMark}`]: {
    fill: palette.grey[200],
  },
  [`.${aviationElementsClasses.airportMarkFlightCategory}`]: {
    fill: 'currentColor',
    filter: 'drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.5))',
  },
  [`.${aviationElementsClasses.airportMarkIFR}`]: {
    color: palette.flightCategory.IFR,
  },
  [`.${aviationElementsClasses.airportMarkLIFR}`]: {
    color: palette.flightCategory.LIFR,
  },
  [`.${aviationElementsClasses.airportMarkMVFR}`]: {
    color: palette.flightCategory.MVFR,
  },
  [`.${aviationElementsClasses.airportMarkVFR}`]: {
    color: palette.flightCategory.VFR,
  },
  [`.organ-type-${ENOPOrganType.Lungs}`]: {
    color: palette.blue[500],
  },
  [`.organ-type-${ENOPOrganType.Heart}`]: {
    color: palette.violet[500],
  },
  [`.organ-type-${ENOPOrganType.Liver}`]: {
    color: palette.green[500],
  },
  [`.${aviationElementsClasses.airportLabel}`]: {
    fill: 'currentColor',
    fontSize: utils.clamp(10, 14, 'sm-xl'),
    fontFamily: '"JetBrains Mono", monospace',
    fontWeight: 500,
    textAnchor: 'middle',
    textShadow:
      '1px 0px 0px rgba(0, 0, 0, 1), -1px 0px 0px rgba(0, 0, 0, 1), 0px 1px 0px rgba(0, 0, 0, 1), 0px -1px 0px rgba(0, 0, 0, 1)',
  },
  [`.${aviationElementsClasses.airportAircraftsMark}`]: {
    textShadow: '0px 0px 3px rgba(0, 0, 0, 1)',
  },
  [`.${aviationElementsClasses.airportAircraftsWrapper}`]: {
    transform: 'translate(0px, 32px)',
    textAnchor: 'middle',
    display: 'none',
    ['.anchorLeft']: {
      transform: 'translate(-16px, 0)',
    },
    [`.${aviationElementsClasses.airportAircraftsMark}`]: {
      fill: palette.grey[200],
    },
  },
  [`.${aviationElementsClasses.trails}`]: {
    [`.${aviationElementsClasses.trail}`]: {
      fill: 'none',
      stroke: 'black',
      opacity: 0,
      strokeWidth: 3,
      transition: 'opacity 250ms ease-in-out',
      [`&.highlighted`]: {
        stroke: 'currentColor',
        opacity: 1,
      },
    },
  },
  [`.${aviationElementsClasses.furtherPaths}`]: {
    [`.${aviationElementsClasses.furtherPath}`]: {
      ['@keyframes dash']: {
        from: {
          strokeDashoffset: 300,
        },
        to: {
          strokeDashoffset: 0,
        },
      },
      strokeDasharray: '4 3',
      fill: 'none',
      opacity: 0.3,
      stroke: 'black',
      strokeWidth: 2,
      transition: 'all 250ms ease-in-out',
      animation: 'dash 10s linear forwards infinite',
      [`&.highlighted`]: {
        stroke: 'currentColor',
        opacity: 1,
      },
    },
  },
  [`.${aviationElementsClasses.aircraftOrphanWrapper}`]: {
    transformOrigin: 'center',
    color: palette.grey[200],
    [`.${aviationElementsClasses.aircraftOrphan}`]: {
      fill: 'currentColor',
      path: {
        fill: 'inherit',
        transform: 'rotate(var(--heading, deg))',
        transformOrigin: 'center',
      },
    },
  },
}));

export const AviationElements = () => {
  // const aircrafts = useAircrafts();
  const { projectionRef, projectionHash } = useGeoMap();
  const {
    aircraftsOnFly,
    airports,
    furtherPaths,
    trails,
    aircraftsByTailNumbers,
  } = useOverlay();

  const highlightedAircraft = useSelectHighlightedAircraftRegistration();
  // Create a ref pointing to the airports group.
  const airportsRef = useRef(null);

  // Store projection function in state to avoid ref access during render
  // Update when projectionHash changes (which tracks projection changes)
  const [projection, setProjection] = useState<d3.GeoProjection | null>(null);

  useEffect(() => {
    setProjection(() => projectionRef.current);
  }, [projectionHash, projectionRef]);

  // Don't render until projection is ready
  if (!projection) {
    return null;
  }

  const projectedTrails = (
    <g name="trails" className={aviationElementsClasses.trails}>
      {Object.entries(trails).map(([registration, { organ, points }]) => {
        const zIndex =
          highlightedAircraft === registration
            ? EZLayers.Highlighted
            : EZLayers.Default;
        return (
          <ZLayer key={registration} zIndex={zIndex}>
            <AviationTrail
              // Drop back-facing vertices (TSMED-1407). A trail that crosses the
              // globe's edge is joined straight across the remaining near points
              // rather than rendering a ghost segment behind the globe.
              points={points
                .filter(p => isGeoPointVisible(projection, p))
                .map(p => projection(p)?.join(','))
                .join(' ')}
              highlighted={highlightedAircraft === registration}
              organ={organ}
            />
          </ZLayer>
        );
      })}
    </g>
  );

  const projectedFurtherPaths = (
    <g name="furtherPaths" className={aviationElementsClasses.furtherPaths}>
      {Object.entries(furtherPaths).map(([registration, { organ, points }]) => {
        const zIndex =
          highlightedAircraft === registration
            ? EZLayers.Highlighted
            : EZLayers.Default;

        return (
          <ZLayer key={registration} zIndex={zIndex}>
            <AviationFurtherPath
              highlighted={highlightedAircraft === registration}
              organ={organ}
              // Drop back-facing vertices — see the trail filter above (TSMED-1407).
              points={points
                .filter(p => isGeoPointVisible(projection, p))
                .map(p => projection(p)?.join(','))
                .join(' ')}
            />
          </ZLayer>
        );
      })}
    </g>
  );
  return (
    <Root>
      {projectedTrails}
      {projectedFurtherPaths}
      <g name="airports" ref={airportsRef}>
        {airports.map(({ aircrafts, id, ICAO, ...rest }) => (
          <AviationAirport
            key={id}
            id={id}
            aircraftTailNumbers={aircrafts}
            aircraftsByTailNumbers={aircraftsByTailNumbers}
            ICAO={ICAO}
            {...rest}
          />
        ))}
      </g>
      <g name="aircraftsOnGround"></g>
      <g name="labels"></g>
      <g name="aircraftsOnFlight">
        {aircraftsOnFly.map(a => (
          <AviationAircraft key={a.Id} aircraftData={a} />
        ))}
      </g>
    </Root>
  );
};
